import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { ConsultationProvider } from "./contexts/ConsultationContext";
import LoginPage from "./pages/LoginPage";
import StaffLoginPage from "./pages/StaffLoginPage";
import DashboardPage from "./pages/DashboardPage";
import PurchaseModule from "./pages/PurchaseModule";
import PharmacyRouter from "./pages/PharmacyRouter";
import PharmacyStoresPage from "./pages/PharmacyStoresPage";
import StoresAdminPage from "./pages/StoresAdminPage";
import SuperAdminLoginPage from "./pages/superadmin/SuperAdminLoginPage";
import SuperAdminLayout from "./pages/superadmin/SuperAdminLayout";
import SuperAdminOverview from "./pages/superadmin/SuperAdminOverview";
import TenantsPage from "./pages/superadmin/TenantsPage";
import PlansPage from "./pages/superadmin/PlansPage";
import BillingPage from "./pages/superadmin/BillingPage";
import PlatformSettingsPage from "./pages/superadmin/PlatformSettingsPage";
import TenantAdminLayout from "./pages/tenantadmin/TenantAdminLayout";
import TenantAdminOverview from "./pages/tenantadmin/OverviewPage";
import TenantAdminUsers from "./pages/tenantadmin/UsersPage";
import TenantAdminBilling from "./pages/tenantadmin/BillingPage";
import TenantAdminDomains from "./pages/tenantadmin/DomainsPage";
import TenantAdminBranding from "./pages/tenantadmin/BrandingPage";
import TenantAdminSettings from "./pages/tenantadmin/SettingsPage";
import TenantAdminCapabilities from "./pages/tenantadmin/CapabilitiesPage";
import SignupPage from "./pages/SignupPage";
import SignupSuccessPage from "./pages/SignupSuccessPage";
import ReceptionPage from "./pages/ReceptionPage";
import ReceptionHomePage from "./pages/ReceptionHomePage";
import InventoryPage from "./pages/InventoryPage";
import PublicClinicLandingPage from "./pages/PublicClinicLandingPage";
import PatientProfilePage from "./pages/PatientProfilePage";
import ReceptionReportsPage from "./pages/ReceptionReportsPage";
import ReceptionRosterPage from "./pages/ReceptionRosterPage";
import ManagementDashboard from "./pages/management/ManagementDashboard";
import FinancialsPage from "./pages/management/FinancialsPage";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import { installAuthInterceptor } from "./utils/axiosAuth";

installAuthInterceptor();

function homeForRole(role) {
  if (role === 'pharmacy') return '/pharmacy';
  if (role === 'pharmacist') return '/pharmacy';
  if (role === 'purchase') return '/purchase';
  if (role === 'pharmacystores') return '/pharmacy-stores';
  if (role === 'storesadmin') return '/stores-admin';
  if (role === 'receptiondesk') return '/reception/home';
  if (role === 'management') return '/management';
  return '/dashboard';
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-700 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user || user === false) {
    // Preserve the tenant query (?tenant=foo) when bouncing back to /login
    // so a session-expired user stays inside their tenant.
    return <Navigate to={`/login${location.search}`} replace />;
  }
  if (allowedRoles && !allowedRoles.includes(user.role || 'doctor')) {
    return <Navigate to={homeForRole(user.role)} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-700 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (user && user !== false) {
    return <Navigate to={homeForRole(user.role)} replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    const suffix =
      p.startsWith('/superadmin')  ? 'Platform' :
      p.startsWith('/login')         ? 'Sign In' :
      p.startsWith('/staff/login')   ? 'Staff Sign In' :
      p.startsWith('/pharmacy/login')? 'Pharmacy Sign In' :
      p.startsWith('/purchase/login')? 'Purchase Sign In' :
      p.startsWith('/dashboard')     ? 'Dashboard' :
      p.startsWith('/pharmacy-stores')? 'Pharmacy Stores' :
      p.startsWith('/stores-admin')  ? 'Stores Admin' :
      p.startsWith('/purchase')      ? 'Purchase' :
      p.startsWith('/pharmacy')      ? 'Pharmacy' :
      p.startsWith('/consultation')  ? 'Consultation' :
      '';
    document.title = suffix ? `EMR | eCare360 · ${suffix}` : 'EMR | eCare360';
  }, [location.pathname]);
  return (
    <Routes>
      {/* SaaS super-admin */}
      <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
      <Route path="/superadmin" element={<SuperAdminLayout />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<SuperAdminOverview />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="settings" element={<PlatformSettingsPage />} />
      </Route>

      {/* Public clinic marketing landing — /c/<slug> */}
      <Route path="/c/:slug" element={<PublicClinicLandingPage />} />

      {/* Tenant-admin self-service portal (gated to role=admin) */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><TenantAdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<TenantAdminOverview />} />
        <Route path="users" element={<TenantAdminUsers />} />
        <Route path="billing" element={<TenantAdminBilling />} />
        <Route path="branding" element={<TenantAdminBranding />} />
        <Route path="capabilities" element={<TenantAdminCapabilities />} />
        <Route path="domains" element={<TenantAdminDomains />} />
        <Route path="settings" element={<TenantAdminSettings />} />
      </Route>

      {/* Public self-serve signup */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/success" element={<SignupSuccessPage />} />

      {/* Tenant-facing app */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/staff/login" element={<PublicRoute><StaffLoginPage /></PublicRoute>} />
      <Route path="/pharmacy/login" element={<PublicRoute><StaffLoginPage role="pharmacy" /></PublicRoute>} />
      <Route path="/purchase/login" element={<PublicRoute><StaffLoginPage role="purchase" /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["doctor","admin"]}><DashboardPage /></ProtectedRoute>} />
      <Route path="/pharmacy" element={<ProtectedRoute allowedRoles={["pharmacy","pharmacist","receptiondesk","admin"]}><PharmacyRouter /></ProtectedRoute>} />
      <Route path="/purchase" element={<ProtectedRoute allowedRoles={["purchase"]}><PurchaseModule /></ProtectedRoute>} />
      <Route path="/pharmacy-stores" element={<ProtectedRoute allowedRoles={["pharmacystores"]}><PharmacyStoresPage /></ProtectedRoute>} />
      <Route path="/stores-admin" element={<ProtectedRoute allowedRoles={["storesadmin"]}><StoresAdminPage /></ProtectedRoute>} />
      <Route path="/reception" element={<ProtectedRoute allowedRoles={["receptiondesk","admin","doctor"]}><ReceptionPage /></ProtectedRoute>} />
      <Route path="/reception/home" element={<ProtectedRoute allowedRoles={["receptiondesk","admin","doctor"]}><ReceptionHomePage /></ProtectedRoute>} />
      <Route path="/reception/reports" element={<ProtectedRoute allowedRoles={["receptiondesk","admin","doctor"]}><ReceptionReportsPage /></ProtectedRoute>} />
      <Route path="/reception/roster" element={<ProtectedRoute allowedRoles={["receptiondesk","admin"]}><ReceptionRosterPage /></ProtectedRoute>} />
      <Route path="/management" element={<ProtectedRoute allowedRoles={["management","admin","receptiondesk"]}><ManagementDashboard /></ProtectedRoute>} />
      <Route path="/financials" element={<ProtectedRoute allowedRoles={["management","admin","doctor"]}><FinancialsPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute allowedRoles={["admin","doctor","receptiondesk","pharmacist","pharmacy"]}><InventoryPage /></ProtectedRoute>} />
      <Route path="/patients/:id" element={<ProtectedRoute allowedRoles={["doctor","admin","receptiondesk","pharmacist","pharmacy"]}><PatientProfilePage /></ProtectedRoute>} />
      <Route path="/" element={<RoleHomeRedirect />} />
      <Route path="*" element={<RoleHomeRedirect />} />
    </Routes>
  );
}

function RoleHomeRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  // Preserve ?tenant=<slug> (and any other query) so the login page keeps
  // the tenant context the user arrived with. Without this, /?tenant=foo
  // would redirect to /login and lose the slug entirely.
  if (!user) return <Navigate to={`/login${location.search}`} replace />;
  return <Navigate to={homeForRole(user.role)} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </TenantProvider>
    </BrowserRouter>
  );
}

export default App;
